// "Put my queue in my Outlook drafts."
//
// ⚠️ THIS REPLACES A DOWNLOAD THAT LOST MAIL. CPx used to build a .eml, hand it to the browser,
// and log the send at the CLICK - so everything after that click was invisible to us. The new
// Outlook takes that file into an Outbox it does not display and never submits it: ~480 of
// Ashley's offers from 27 August, and 10 more on 17 September. Greg, 2026-09-21: "why can't you
// just draft the reply like you draft for me and skip that step."
//
// ⚠️ THE BODY IS STILL BUILT HERE, BY THE SAME BUILDERS THE COMPOSER USES. The server never
// composes a VIP email - if it did there would be two versions of the copy and they would drift,
// which is the mirror-drift trap this repo already carries scars from. The browser builds it and
// posts it; the server only decides WHO it may be addressed to and WHOSE mailbox it lands in.
//
// ⚠️ THE CLAIM STILL RUNS FIRST, ONE CUSTOMER AT A TIME, AND A REFUSAL STOPS THAT CUSTOMER. That
// ordering is load-bearing: it is what stopped 50 people being offered the same thing twice on
// 2026-09-16. A blocked claim must never produce a draft.
import { logVipOutreach } from '../services/api';
import { getAccessToken } from '../services/auth';
import { newSendToken, trackingReady, pixelTag } from './outreachEmail';

const base = () => (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions');

async function postDrafts(payload) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/outreach-draft', {
    method: 'POST', headers, body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'draft ' + res.status);
  return body;
}

// The server takes 60 at a time; a win-back queue is 50 and delivered-by-day is uncapped, so
// anything longer is posted in chunks rather than refused.
const CHUNK = 50;

/**
 * Claim each customer, build their email, and put the lot in the rep's Outlook Drafts.
 *
 * @param jobs  [{ key, claim(track) -> row|{blocked}, build(track) -> {subject, html} }]
 *              `claim` is the caller's existing vip_outreach_add payload, unchanged.
 * @param onProgress  called with (done, total) so a queue of 50 does not look frozen.
 * @returns { created, blocked: [{key, reason}], failed: [...], mailbox }
 */
// `target`: stop once this many have been claimed. The win-back passes its whole queue with
// target 50, so a refusal (already offered under a linked account, Do not contact...) moves on to
// the next customer instead of leaving the rep at 45 (Rachael, 2026-10-21).
export async function draftIntoOutlook(jobs, { onProgress, chunk = CHUNK, target = Infinity } = {}) {
  // Asked ONCE for the whole batch, not per customer: it is a network probe, and fifty of them
  // would add seconds to a run for an answer that cannot change mid-batch.
  const embedded = await trackingReady();

  const items = [];
  const blocked = [];

  const total = Number.isFinite(target) ? Math.min(target, jobs.length) : jobs.length;
  for (const job of jobs) {
    if (items.length >= target) break;
    const track = { token: newSendToken(), embedded };
    let row;
    try { row = await job.claim(track); }
    catch (e) { blocked.push({ key: job.key, reason: 'Could not record it: ' + e.message }); continue; }
    finally { if (onProgress) onProgress(Math.min(items.length + 1, total), total); }

    if (row && row.blocked) { blocked.push({ key: job.key, reason: row.reason }); continue; }
    // vip_outreach_add returns the inserted row(s); the id is what the draft is keyed to.
    const logId = Array.isArray(row) ? (row[0] && row[0].id) : (row && row.id);
    if (!logId) { blocked.push({ key: job.key, reason: 'No send id came back, so nothing was drafted.' }); continue; }

    const built = job.build(track);
    items.push({
      logId,
      subject: built.subject,
      html: embedded ? built.html + pixelTag(track.token) : built.html,
    });
  }

  // `chunk` is smaller for a threaded reply: that is three Graph calls a draft instead of one,
  // and the endpoint has to finish inside a single request.
  let created = 0; let threaded = 0; let moved = 0; const failed = []; const skipped = []; let mailbox = null;
  // ⚠️ ONE BAD BATCH MUST NOT STOP THE RUN. Every item here is already claimed, so stopping left
  // customers counted as followed up with no draft (Rachael, Nov 2, 2026: 8 of them, off a 504).
  // What the server did not get to comes back in `retry`. A batch that errors is NOT re-posted,
  // because a draft may have been written before the error: it goes back on her list, and two
  // errors in a row give up on the rest the same way.
  const pending = [...items];
  const giveBack = [];
  let errorsInARow = 0;
  while (pending.length) {
    const batch = pending.splice(0, chunk);
    let r;
    try {
      r = await postDrafts({ items: batch });
      errorsInARow = 0;
    } catch (e) {
      errorsInARow += 1;
      giveBack.push(...batch.map((b) => b.logId));
      failed.push(...batch.map((b) => ({ logId: b.logId, error: e.message })));
      if (errorsInARow >= 2) {
        giveBack.push(...pending.map((b) => b.logId));
        failed.push(...pending.map((b) => ({ logId: b.logId, error: e.message })));
        pending.length = 0;
      }
      continue;
    }
    created += Number(r.created || 0);
    threaded += Number(r.threaded || 0);
    moved += Number(r.moved || 0);
    failed.push(...(r.failed || []));
    skipped.push(...(r.skipped || []));
    mailbox = r.mailbox || mailbox;
    const retry = new Set((r.retry || []).map(Number));
    if (retry.size) {
      const again = batch.filter((b) => retry.has(Number(b.logId)));
      if (again.length === batch.length && !r.created) {
        // Took the whole batch back with nothing done, so another try would go the same way.
        giveBack.push(...again.map((b) => b.logId));
        failed.push(...again.map((b) => ({ logId: b.logId, error: 'Outlook was too slow' })));
      } else {
        pending.unshift(...again);
      }
    }
  }
  if (giveBack.length) {
    try { await postDrafts({ release: giveBack }); } catch (_) { /* the screen's next load sweeps them */ }
  }
  return { created, threaded, moved, blocked, failed, skipped, mailbox, claimed: items.length };
}
